import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { Download, ShieldAlert, ShieldCheck } from 'lucide-react';
import { downloadGet, get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateTime, humanize } from '@/lib/utils';
import { DataTable, Dialog, FilterField, PageHeader, SearchFilterBar } from '@/components/ui/layout';
import { Alert, Badge, Button, DatePicker, Input, Select, SuggestionPopover } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { useDebounced } from '@/features/diagnostics/LabBenchDebounce';

interface AuditRow {
  id: string;
  seq: number;
  actorId: string | null;
  actorName: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  ip: string | null;
  reason: string | null;
  requestId: string | null;
  hash: string | null;
  createdAt: string;
}

interface VerifyResult {
  intact: boolean;
  brokenAt: string[];
  rowsChecked: number;
}

const RECORD_TYPES: { value: string; label: string }[] = [
  { value: 'patients', label: 'Patient' },
  { value: 'appointments', label: 'Appointment' },
  { value: 'encounters', label: 'Consultation' },
  { value: 'prescriptions', label: 'Prescription' },
  { value: 'admissions', label: 'Admission' },
  { value: 'lab_orders', label: 'Lab order' },
  { value: 'invoices', label: 'Invoice' },
  { value: 'payments', label: 'Payment' },
  { value: 'refunds', label: 'Refund' },
  { value: 'users', label: 'Staff account' },
  { value: 'user_roles', label: 'Role assignment' },
  { value: 'roles', label: 'Role' },
  { value: 'branches', label: 'Branch' },
  { value: 'departments', label: 'Department' },
  { value: 'tenant_settings', label: 'Settings' },
];

const ACTIONS = ['create', 'update', 'delete', 'login', 'logout', 'view_sensitive', 'break_glass', 'export'];

function recordLabel(entityType: string): string {
  return RECORD_TYPES.find((type) => type.value === entityType)?.label ?? humanize(entityType);
}

function startOfDay(date: string): string | undefined {
  return date ? new Date(`${date}T00:00:00`).toISOString() : undefined;
}

function endOfDay(date: string): string | undefined {
  return date ? new Date(`${date}T23:59:59.999`).toISOString() : undefined;
}

function isoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/**
 * Tenant audit trail (SRS 4.3.1, 14.1).
 *
 * The chain check is on the same screen as the entries, because "here is the
 * log" and "here is the proof it was not edited" are the same question.
 */
export function AuditPage() {
  const { user: session } = useAuth();
  const toast = useToast();
  const onlineCopy = session?.readOnlyReason === 'cloud_mirror';
  const [params] = useSearchParams();
  const [entityType, setEntityType] = useState(params.get('entityType') ?? '');
  const [entityId, setEntityId] = useState(params.get('entityId') ?? '');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [actor, setActor] = useState<{ id: string; name: string } | null>(null);
  const [actorSearch, setActorSearch] = useState('');
  const [peopleOpen, setPeopleOpen] = useState(false);
  const [selected, setSelected] = useState<AuditRow | null>(null);
  const [exporting, setExporting] = useState(false);

  const filters = {
    entityType: entityType || undefined,
    entityId: entityId || undefined,
    action: action || undefined,
    actorId: actor?.id,
    from: startOfDay(from),
    to: endOfDay(to),
  };

  const logs = useInfiniteQuery({
    queryKey: ['audit', filters],
    initialPageParam: '',
    queryFn: ({ pageParam }) =>
      get<{ data: AuditRow[]; nextCursor: number | string | null }>(
        `/org/audit-logs${qs({ ...filters, limit: 50, cursor: pageParam || undefined })}`,
      ),
    getNextPageParam: (last) => (last.nextCursor === null || last.nextCursor === undefined ? undefined : String(last.nextCursor)),
  });
  const rows = logs.data?.pages.flatMap((page) => page.data);

  const settledSearch = useDebounced(actorSearch.trim(), 300);
  const people = useQuery({
    queryKey: ['audit-people', settledSearch],
    queryFn: () => get<{ data: { id: string; name: string }[] }>(`/org/users${qs({ q: settledSearch, limit: 10 })}`),
    enabled: settledSearch.length >= 2 && !actor,
  });

  const verifyRange = () => {
    const end = to || isoDate(new Date());
    const start = from || isoDate(new Date(Date.now() - 29 * 24 * 60 * 60 * 1000));
    return { from: startOfDay(start) as string, to: endOfDay(end) as string, label: tr('admin', 'x_to_x', { start: start, end: end }) };
  };

  const verify = useMutation({
    mutationFn: () => {
      const range = verifyRange();
      return post<VerifyResult>('/org/audit-logs/verify', { from: range.from, to: range.to });
    },
    onError: (caught) => toast.error(tr('admin', 'could_not_check_the_audit_trail'), describeError(caught)),
  });

  const exportCsv = async () => {
    setExporting(true);
    try {
      await downloadGet(`/org/audit-logs/export.csv${qs(filters)}`, `audit-log-${isoDate(new Date())}.csv`);
    } catch (caught) {
      toast.error(tr('admin', 'could_not_export_the_audit_trail'), describeError(caught));
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <PageHeader
        title={tr('admin', 'audit_log')}
        description={tr('admin', 'every_create_update_and_delete_on_clinical_and')}
        actions={
          !onlineCopy && (
            <Button variant="outline" loading={verify.isPending} onClick={() => verify.mutate()}>
              <ShieldCheck className="h-4 w-4" /> {tr('admin', 'verify_integrity')}
            </Button>
          )
        }
      />

      {verify.data && (
        <Alert
          variant={verify.data.intact ? 'success' : 'destructive'}
          className="mb-4"
          title={verify.data.intact ? tr('admin', 'the_audit_trail_verifies') : tr('admin', 'the_audit_chain_does_not_verify')}
        >
          <span className="inline-flex items-center gap-1">
            {verify.data.intact ? <ShieldCheck className="h-3.5 w-3.5" /> : <ShieldAlert className="h-3.5 w-3.5" />}
            {verify.data.intact
              ? tr('admin', 'x_entries_checked_none_altered', { rowsChecked: verify.data.rowsChecked })
              : tr('admin', 'x_of_x_entries_fail_verification_entries_have', { length: verify.data.brokenAt.length, rowsChecked: verify.data.rowsChecked })}
          </span>
          {!from && !to && <span className="block text-xs">{tr('admin', 'checked_the_last_30_days_pick_dates_above_to')}</span>}
        </Alert>
      )}

      <SearchFilterBar
        search={{ value: entityId, onChange: setEntityId, label: tr('admin', 'record_id'), className: 'sm:max-w-xs' }}
        filters={
          <>
            <FilterField label={tr('admin', 'from')} className="w-40">
              <DatePicker value={from} onChange={setFrom} />
            </FilterField>
            <FilterField label={tr('admin', 'to')} className="w-40">
              <DatePicker value={to} onChange={setTo} />
            </FilterField>
            <FilterField label={tr('admin', 'person')} className="w-56">
              {actor ? (
                <div className="flex h-9 items-center justify-between rounded-md border px-2 text-sm">
                  <span className="truncate">{actor.name}</span>
                  <button type="button" className="text-xs text-primary hover:underline focus-ring" onClick={() => setActor(null)}>
                    {tr('admin', 'clear')}
                  </button>
                </div>
              ) : (
                <SuggestionPopover
                  open={peopleOpen && (people.data?.data.length ?? 0) > 0}
                  onDismiss={() => setPeopleOpen(false)}
                  anchor={
                    <Input
                      id="audit-person-search"
                      placeholder={tr('admin', 'search_staff')}
                      value={actorSearch}
                      onFocus={() => setPeopleOpen(true)}
                      onChange={(e) => {
                        setActorSearch(e.target.value);
                        setPeopleOpen(true);
                      }}
                    />
                  }
                >
                  <div className="max-h-64 overflow-y-auto p-1">
                    {people.data?.data.map((person) => (
                      <button
                        key={person.id}
                        type="button"
                        className="block w-full rounded px-2 py-1.5 text-start text-sm transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
                        onClick={() => {
                          setActor(person);
                          setActorSearch('');
                          setPeopleOpen(false);
                        }}
                      >
                        {person.name}
                      </button>
                    ))}
                  </div>
                </SuggestionPopover>
              )}
            </FilterField>
            <FilterField label={tr('admin', 'record_type')} className="w-44">
              <Select value={entityType} onChange={(e) => setEntityType(e.target.value)}>
                <option value="">{tr('admin', 'all_records')}</option>
                {entityType && !RECORD_TYPES.some((type) => type.value === entityType) && (
                  <option value={entityType}>{humanize(entityType)}</option>
                )}
                {RECORD_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Select>
            </FilterField>
            <FilterField label={tr('admin', 'action')} className="w-40">
              <Select value={action} onChange={(e) => setAction(e.target.value)}>
                <option value="">{tr('admin', 'all_actions')}</option>
                {ACTIONS.map((a) => (
                  <option key={a} value={a}>
                    {humanize(a)}
                  </option>
                ))}
              </Select>
            </FilterField>
          </>
        }
        trailing={
          <Button variant="secondary" loading={exporting} onClick={() => void exportCsv()}>
            <Download className="h-4 w-4" /> {tr('admin', 'export_csv')}
          </Button>
        }
        onReset={
          entityType || entityId || action || from || to || actor
            ? () => {
                setEntityType('');
                setEntityId('');
                setAction('');
                setFrom('');
                setTo('');
                setActor(null);
                setActorSearch('');
              }
            : undefined
        }
      />

      <DataTable
        rows={rows}
        loading={logs.isLoading} refreshing={logs.isFetching}
        partial={Boolean(logs.hasNextPage)}
        error={logs.error}
        rowKey={(row) => row.id}
        onRowClick={(row) => setSelected(row)}
        dense
        empty={tr('admin', 'no_audit_entries_match')}
        columns={[
          { header: tr('admin', 'when'), sort: (row) => row.createdAt, cell: (row) => dateTime(row.createdAt) },
          { header: tr('admin', 'person'), sort: (row) => row.actorName, cell: (row) => row.actorName ?? <span className="text-muted-foreground">{tr('admin', 'system')}</span> },
          {
            header: tr('admin', 'action'),
            sort: (row) => row.action,
            cell: (row) => (
              <Badge
                variant={
                  row.action === 'delete' ? 'destructive' : row.action === 'break_glass' ? 'warning' : 'secondary'
                }
              >
                {humanize(row.action)}
              </Badge>
            ),
          },
          { header: tr('admin', 'record'), sort: (row) => row.entityType, cell: (row) => recordLabel(row.entityType) },
          { header: tr('admin', 'ip'), sort: (row) => row.ip, cell: (row) => row.ip ?? '—' },
          { header: tr('admin', 'reason'), sort: (row) => row.reason, cell: (row) => row.reason ?? '—' },
        ]}
        footer={
          logs.hasNextPage && (
            <Button variant="secondary" size="sm" loading={logs.isFetchingNextPage} onClick={() => void logs.fetchNextPage()}>
              {tr('admin', 'load_more')}
            </Button>
          )
        }
      />

      <Dialog
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        wide
        title={selected ? tr('admin', 'x_on_x', { action: humanize(selected.action), entityType: recordLabel(selected.entityType) }) : ''}
        description={selected ? `${dateTime(selected.createdAt)} · ${selected.actorName ?? 'system'}` : undefined}
      >
        {selected && (
          <div className="space-y-4 text-sm">
            <div className="grid gap-2 sm:grid-cols-2">
              <Detail label={tr('admin', 'record_id')} value={selected.entityId ?? '—'} mono />
              <Detail label={tr('admin', 'request_id')} value={selected.requestId ?? '—'} mono />
              <Detail label={tr('admin', 'ip_address')} value={selected.ip ?? '—'} />
              <Detail label={tr('admin', 'sequence')} value={String(selected.seq)} />
              <Detail label={tr('admin', 'hash')} value={selected.hash ?? '—'} mono className="sm:col-span-2" />
            </div>

            <ChangedFields before={selected.before} after={selected.after} />

            <div className="grid gap-3 lg:grid-cols-2">
              <JsonBlock title={tr('admin', 'before')} value={selected.before} />
              <JsonBlock title={tr('admin', 'after')} value={selected.after} />
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
}

function display(value: unknown): string {
  if (value === undefined) return '—';
  if (value === null) return 'empty';
  return typeof value === 'string' ? value : JSON.stringify(value);
}

function ChangedFields({ before, after }: { before: Record<string, unknown> | null; after: Record<string, unknown> | null }) {
  const keys = [...new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])].filter(
    (key) => key !== 'updatedAt' && JSON.stringify(before?.[key]) !== JSON.stringify(after?.[key]),
  );
  if (keys.length === 0) return null;
  return (
    <div>
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{tr('admin', 'changed_fields')}</p>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-xs">
          <thead className="bg-muted/40 text-start">
            <tr>
              <th className="px-2 py-1 font-medium">{tr('admin', 'field')}</th>
              <th className="px-2 py-1 font-medium">{tr('admin', 'before')}</th>
              <th className="px-2 py-1 font-medium">{tr('admin', 'after')}</th>
            </tr>
          </thead>
          <tbody>
            {keys.map((key) => (
              <tr key={key} className="border-t align-top">
                <td className="px-2 py-1 font-medium">{humanize(key)}</td>
                <td className="max-w-[16rem] break-all px-2 py-1 font-mono">{before ? display(before[key]) : '—'}</td>
                <td className="max-w-[16rem] break-all px-2 py-1 font-mono">{after ? display(after[key]) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Detail({ label, value, mono, className }: { label: string; value: string; mono?: boolean; className?: string }) {
  return (
    <div className={className}>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={mono ? 'break-all font-mono text-xs' : ''}>{value}</p>
    </div>
  );
}

function JsonBlock({ title, value }: { title: string; value: Record<string, unknown> | null }) {
  return (
    <div>
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
      <pre className="max-h-64 overflow-auto rounded-md border bg-muted/40 p-2 text-xs">
        {value ? JSON.stringify(value, null, 2) : '—'}
      </pre>
    </div>
  );
}
